import { useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  DollarSign,
  Link2,
  Brain,
  Server,
  BarChart3,
  ShieldCheck,
  Activity,
  Database,
  Bot,
  UserCheck,
  AlertTriangle,
  Lock,
  Target,
  CheckCircle2,
  Ticket,
  ClipboardCheck,
  GraduationCap,
  Mail,
  Gauge,
  Layers,
  Network,
  BookOpen,
  Boxes,
  // Users — kept for potential future section icons
} from 'lucide-react'

const PLAN_PASSWORD = 'dizee 2026'

type TaskType = 'ai' | 'manual'
type BuildStatus =
  | 'complete'
  | 'exists-needs-work'
  | 'partially-built'
  | 'not-built'

interface TaskItem {
  title: string
  description: string
  type: TaskType
}

interface DecisionItem {
  title: string
  description: string
}

interface Section {
  id: string
  number: number
  icon: typeof DollarSign
  title: string
  subtitle: string
  status: BuildStatus
  statusBanner: string
  color: string
  tasks: TaskItem[]
  decisions?: DecisionItem[]
  /** Production-roadmap: goal, constraints, ordering, and definition of done */
  purpose?: string
  implementationConsiderations?: string[]
  dependencies?: string[]
  roadmapPlacement?: string
  doneCriteria?: string[]
}

const statusConfig: Record<BuildStatus, { label: string; bg: string; text: string; dot: string }> = {
  complete: { label: 'Complete', bg: 'bg-emerald-500/10', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  'exists-needs-work': { label: 'Exists — Needs Work', bg: 'bg-amber-500/10', text: 'text-amber-400', dot: 'bg-amber-400' },
  'partially-built': { label: 'Partially Built', bg: 'bg-sky-500/10', text: 'text-sky-400', dot: 'bg-sky-400' },
  'not-built': { label: 'Not Built', bg: 'bg-rose-500/10', text: 'text-rose-400', dot: 'bg-rose-400' },
}

const colorMap: Record<string, { border: string; bg: string; text: string; pill: string; bannerBg: string }> = {
  sky: {
    border: 'border-sky-500/20',
    bg: 'bg-sky-500/[0.04]',
    text: 'text-sky-400',
    pill: 'bg-sky-500/15 text-sky-300',
    bannerBg: 'bg-sky-500/[0.06] border-sky-500/15',
  },
  violet: {
    border: 'border-violet-500/20',
    bg: 'bg-violet-500/[0.04]',
    text: 'text-violet-400',
    pill: 'bg-violet-500/15 text-violet-300',
    bannerBg: 'bg-violet-500/[0.06] border-violet-500/15',
  },
  emerald: {
    border: 'border-emerald-500/20',
    bg: 'bg-emerald-500/[0.04]',
    text: 'text-emerald-400',
    pill: 'bg-emerald-500/15 text-emerald-300',
    bannerBg: 'bg-emerald-500/[0.06] border-emerald-500/15',
  },
  amber: {
    border: 'border-amber-500/20',
    bg: 'bg-amber-500/[0.04]',
    text: 'text-amber-400',
    pill: 'bg-amber-500/15 text-amber-300',
    bannerBg: 'bg-amber-500/[0.06] border-amber-500/15',
  },
  rose: {
    border: 'border-rose-500/20',
    bg: 'bg-rose-500/[0.04]',
    text: 'text-rose-400',
    pill: 'bg-rose-500/15 text-rose-300',
    bannerBg: 'bg-rose-500/[0.06] border-rose-500/15',
  },
  cyan: {
    border: 'border-cyan-500/20',
    bg: 'bg-cyan-500/[0.04]',
    text: 'text-cyan-400',
    pill: 'bg-cyan-500/15 text-cyan-300',
    bannerBg: 'bg-cyan-500/[0.06] border-cyan-500/15',
  },
}

/* ═══════════════════════════════════════════════════════════════════════════
   ALREADY-COMPLETE WORK (acknowledged, not in active plan)
   ═══════════════════════════════════════════════════════════════════════════ */
const completedWork = [
  {
    title: 'Consolidated Experience (Replaces Promoter / Manager Dashboards)',
    description:
      'All user types (artist, manager, support, promoter) share the same DIZEE experience. No separate dashboards. Users toggle on/off accounts in Team Hub, and every page (Shows, Music, Tasks, Money) already filters by active artist(s). Permission-based sidebar visibility gates which sections each role can see.',
    files: [
      'TeamHubPage.tsx — toggle on/off, request access, pending artists, promoter artist accounts, leave team',
      'TeamPage.tsx — My Accounts / Manage Team tabs',
      'Sidebar.tsx — dynamic visibility based on permissions + active artists',
      'useUserPermissions.ts — section-level gating per role',
      'ShowsPage / MusicPage / TasksPage / MoneyPage — filterArtistId dropdown, multi-artist fetch, dashboardArtistId',
    ],
  },
  {
    title: 'Multi-Team Filtering Across All Sections',
    description:
      'When a user is on multiple teams, every section loads data per active artist and renders a filter dropdown. Add forms include a team/artist selector. Data is tagged with dashboardArtistId for filtering.',
    files: [
      'api.ts — getActiveArtistIds(), toggleActiveArtistId(), setActiveArtistIds(), enterArtistContext()',
      'ShowsPage.tsx — Promise.all per artist, filterArtistId, permission-filtered shows',
      'MusicPage.tsx — same pattern, filterArtistId',
      'MoneyPage.tsx — same pattern, filterArtistId, formTeamArtistId',
      'TasksPage.tsx — filterArtistId',
    ],
  },
  {
    title: 'Activity System (Fully Built)',
    description:
      'Dual-write to localStorage + backend API. Tracks add/edit/delete/submit across shows, tasks, music, finance. Approve/deny flow for visibility changes (private → team). Progress bar. All sections wired in.',
    files: [
      'ActivityPage.tsx — feed, filters, approve/deny, progress',
      'activityApi.ts — backend client',
      'activityLog.ts — dual-write utility',
      'backend-dizee/src/Routes/activity.ts — list, create, progress, update status',
      'backend-dizee/src/controllers/activity.ts — full CRUD',
    ],
  },
  {
    title: 'Show → Money Guarantee Flow (Backend Cron + Frontend)',
    description:
      'Backend showFinancialsService runs at 4 AM PST. Marks past shows as completed, creates FinancialEntry from guarantees (revenue, paid, fromShows: true, bookingId). Frontend also generates virtual entries from bookings and de-duplicates against API entries. Both upcoming (unpaid) and past (paid) shows handled.',
    files: [
      'backend-dizee/src/services/showFinancialsService.ts — markPastShowsCompletedSimple(), ensureFinancialEntriesForCompletedShows(), upsertUnpaidShowEntry()',
      'backend-dizee/src/services/cronService.ts — scheduled run',
      'MoneyPage.tsx — fetchFromShowsEntries(), allEntries de-duplication',
    ],
  },
  {
    title: 'Backend Analytics Tracking (Custom)',
    description:
      'Session tracking, page views, interactions, scroll depth. Admin-only daily analytics reports and email reports. Not GA4 but covers in-app usage tracking.',
    files: [
      'backend-dizee/src/Routes/analyticsTracking.ts — session/start, session/end, heartbeat, track/app-usage, track/page-view, track/interaction, track/scroll-depth',
      'backend-dizee/src/controllers/analyticsController.ts — getDailyAnalytics, sendTestReport, sendDailyReport',
    ],
  },
  {
    title: 'Backend Admin Endpoints (Partial)',
    description:
      'Admin routes exist: db-seed, login, me, getUsers. Admin-only middleware (adminOnly) gates analytics and report endpoints. Foundation for a full admin dashboard.',
    files: [
      'backend-dizee/src/Routes/admin.ts — db-seed, login, me, users',
      'backend-dizee/src/middleware/adminAuth.ts — adminOnly middleware',
    ],
  },
  {
    title: 'Sync Status UI',
    description:
      'SyncStatusDot component renders synced/pending/out_of_sync states. Used on Shows, Tasks, Music, and Money pages. Data flows from backend syncStatus field.',
    files: [
      'SyncStatusDot.tsx — green/orange/red dot',
      'normalizeBookings.ts — maps syncStatus from API',
    ],
  },
  {
    title: 'Backend Team & Permissions Architecture',
    description:
      'Full team management: TeamMember model (artistId ↔ teamMemberId, role, permissions, sections, financialAccess), TeamInvitation, PromoterRequest, PendingArtist. Delegated access pattern (getDelegatedAccessContext) for multi-account. Financial access middleware (checkFinancialAccess). Promoter routes (shows, invite-artist).',
    files: [
      'backend-dizee/src/database/models/TeamMember.ts',
      'backend-dizee/src/database/models/TeamInvitation.ts',
      'backend-dizee/src/database/models/PromoterRequest.ts',
      'backend-dizee/src/database/models/PendingArtist.ts',
      'backend-dizee/src/helper/delegatedAccess.ts',
      'backend-dizee/src/middleware/financialFilter.ts',
      'backend-dizee/src/Routes/promoter.ts',
    ],
  },
]

/* ═══════════════════════════════════════════════════════════════════════════
   REMAINING WORK — SECTION DATA
   ═══════════════════════════════════════════════════════════════════════════ */
const sections: Section[] = [
  /* ── 1 — DIZEE Links ───────────────────────────────────────────────── */
  {
    id: 'links',
    number: 1,
    icon: Link2,
    title: 'DIZEE Links',
    subtitle: 'Shareable links for data collection, releases, and future link types',
    status: 'exists-needs-work',
    statusBanner:
      'LinksPage is fully built with create flow, 4 summary boxes (Total Links, Active, Engagement, Pre-saves), spreadsheet table, and two link types: Data Collection and Release. Public pages work at /l/collect/:linkId and /l/release/:slug. CollectLinkPage has all planned fields (legal name, release date, splits, master/publishing, artwork, label copy). HOWEVER: Links is hidden in the sidebar (filtered out in Sidebar.tsx). All data is localStorage only — no backend persistence. LinkDetailPage exists for viewing submissions.',
    color: 'emerald',
    tasks: [
      {
        title: 'Unhide Links in the sidebar',
        description:
          'In Sidebar.tsx, Links is explicitly filtered out with: if (item.href === \'/links\') return false. Remove this filter to make Links visible in the navigation.',
        type: 'ai',
      },
      {
        title: 'Add backend persistence for links',
        description:
          'Currently linksStore.ts stores everything in localStorage. Build API endpoints to persist links and submissions server-side. Keep localStorage as a cache/fallback but make the backend the source of truth. Needs a Links model in backend-dizee.',
        type: 'ai',
      },
      {
        title: 'Auto-create upcoming release on data collection submission',
        description:
          'When a data collection form is submitted via CollectLinkPage, create a corresponding release entry in Music with the collected metadata. This bridges Links → Music.',
        type: 'ai',
      },
      {
        title: 'Wire pre-save and click data to backend',
        description:
          'When fans interact with release links (pre-saves, platform clicks), store engagement events in the backend. Currently clickCount/submissionCount/preSaveCount exist on the link object but only in localStorage.',
        type: 'ai',
      },
      {
        title: 'Test links end-to-end: create → share URL → submit form → verify data',
        description:
          'Create a data collection link and a release link. Open the public URLs. Submit data through the collection form. Open the release link and test pre-save. Verify all data flows back.',
        type: 'manual',
      },
    ],
    decisions: [
      {
        title: 'Backend for links: extend external API or local Express backend?',
        description:
          'Links currently use localStorage. Should we add link endpoints to backend-dizee (which already handles bookings, catalog, financials, team, activity) or build a separate service?',
      },
    ],
  },

  /* ── 2 — Money Section Enhancements ────────────────────────────────── */
  {
    id: 'money',
    number: 2,
    icon: DollarSign,
    title: 'Money Section Enhancements',
    subtitle: 'Summary boxes, time-range filters, status types, attachments, running balance',
    status: 'exists-needs-work',
    statusBanner:
      'MoneyPage is fully built (~1400 lines) with CRUD, paid/unpaid toggling, search/filter by type and category, bulk delete, multi-artist filtering, and show guarantee entries flowing in via fetchFromShowsEntries + backend cron. GAPS: Summary boxes are Week/Month/YTD instead of Income/Expenses/Projected Net/Cash Flow. No time-range filters (7/14/30/90 days). Only revenue/spend types — no Planned/Invoiced/Past Due/Paid/Received statuses. No attachment support. No commission entries. No running balance column.',
    color: 'emerald',
    tasks: [
      {
        title: 'Replace summary boxes with plan layout',
        description:
          'Replace current Week/Month/YTD summary boxes with: Income, Expenses, Projected Net, Cash Flow. These should update dynamically based on the selected timeframe filter.',
        type: 'ai',
      },
      {
        title: 'Add time-range filter bar',
        description:
          'Add filter options: All, Next 7 days, Next 14 days, Next 30 days, Next 90 days, Next 6 months, Next 12 months. Control which transactions are visible and update the summary boxes.',
        type: 'ai',
      },
      {
        title: 'Add transaction status types',
        description:
          'Replace the is_paid boolean with full status types: Planned, Invoiced, Past Due, Paid, Received. Add status column to spreadsheet and status filter. Backend FinancialEntry model already stores type/isPaid but needs a new status field.',
        type: 'ai',
      },
      {
        title: 'Add attachment support (links only, no file upload)',
        description:
          'Each transaction should allow attaching documentation links: PDF URL, Google Drive, Dropbox, Notion, or general external link. No file upload — just link fields. Lightweight approach.',
        type: 'ai',
      },
      {
        title: 'Add running balance column',
        description:
          'Add a Running Balance column that calculates cumulatively based on sorted transactions (income adds, expenses subtract).',
        type: 'ai',
      },
      {
        title: 'Add commission entry type',
        description:
          'Add a "Commissions" action alongside Add Income and Add Expense. Commissions should eventually auto-calculate from show commission rules.',
        type: 'ai',
      },
      {
        title: 'Review Money page with real financial data',
        description:
          'After enhancements, load real financial entries and verify: summary boxes calculate correctly, filters work, statuses display, attachments save, running balance is accurate.',
        type: 'manual',
      },
    ],
  },

  /* ── 3 — Money ↔ Shows Financial Architecture ─────────────────────── */
  {
    id: 'money-shows',
    number: 3,
    icon: DollarSign,
    title: 'Money ↔ Shows: Beyond Guarantees',
    subtitle: 'Extend show-to-Money flow for bonuses, commissions, expenses, payment schedules',
    status: 'partially-built',
    statusBanner:
      'The guarantee → Money pipeline is solid: backend cron (showFinancialsService) creates/updates FinancialEntry records, frontend de-duplicates virtual vs API entries. Show model in the backend already has financial fields: guarantee, walkout_potential, gross_potential, net_potential, artist_net, artist_profit, split_point, amount_to_split, deal_type, deal_info, deposit_due, deposit_received, total_fees, currency_conversion. ShowsPage form exposes these as basic + advanced fields. GAPS: Only guarantee flows to Money. Bonuses, commissions, travel, walkout potential don\'t create derived transactions. No payment schedule support (deposit + settlement as separate entries).',
    color: 'amber',
    tasks: [
      {
        title: 'Extend showFinancialsService to create entries from more fields',
        description:
          'Currently only guarantee creates a FinancialEntry. Extend to also create entries from: walkout_potential (projected income), deposit_due (expense if outbound), deposit_received (income). Map each to the correct type and category. The backend Bookings model already has these fields.',
        type: 'ai',
      },
      {
        title: 'Add bonus and commission fields to the show form',
        description:
          'The backend Bookings model doesn\'t currently have bonus or commission fields. Add these to the model and to the ShowsPage add/edit form. Then extend showFinancialsService to create derived Money entries from them.',
        type: 'ai',
      },
      {
        title: 'Build payment schedule support',
        description:
          'Shows often have multiple payments (deposit, settlement, bonus payout). Add a payment schedule array to the Bookings model. Each payment schedule item should generate its own Money transaction with the correct due date for accurate cash flow projections.',
        type: 'ai',
      },
      {
        title: 'Verify show-to-Money flow with real shows',
        description:
          'Create shows with guarantees, bonuses, commissions, and multi-payment schedules. Verify each creates the correct Money entries with correct dates, amounts, and types.',
        type: 'manual',
      },
    ],
  },

  /* ── 4 — Ticket Count History ──────────────────────────────────────── */
  {
    id: 'ticket-history',
    number: 4,
    icon: Ticket,
    title: 'Ticket Count History & Pacing',
    subtitle: 'Historical ticket count logging for pacing analysis and intelligence',
    status: 'not-built',
    statusBanner:
      'Shows have a ticket counter (tix_sold field) and the form supports entering ticket counts. However, every update overwrites the previous value — there is no historical log of ticket count changes over time. The plan requires every ticket count update to log: ticket value, date, time, submitting user. This builds the pacing dataset the intelligence layer will use.',
    color: 'rose',
    tasks: [
      {
        title: 'Create TicketHistory model in backend',
        description:
          'Create a TicketHistory collection that stores every ticket count update: bookingId, ticketCount, previousCount, timestamp, submittingUserId, submittingUserEmail. Index on bookingId + timestamp for efficient pacing queries.',
        type: 'ai',
      },
      {
        title: 'Add ticket history API endpoints',
        description:
          'Add endpoints: POST to create a ticket history entry (called whenever tix_sold is updated), GET to retrieve ticket history for a booking (for pacing charts).',
        type: 'ai',
      },
      {
        title: 'Wire ticket count updates to create history entries',
        description:
          'In the ShowsPage ticket counter update flow and the backend booking update endpoint, create a TicketHistory entry every time tix_sold changes. Include the previous value for delta calculation.',
        type: 'ai',
      },
      {
        title: 'Build ticket pacing visualization',
        description:
          'Add a pacing chart/view to the show detail or dropdown that displays ticket count over time. This is the foundation for the intelligence layer\'s pacing analysis.',
        type: 'ai',
      },
      {
        title: 'Test ticket history with real show data',
        description:
          'Update ticket counts on a show multiple times over several days. Verify the history log captures every change and the pacing visualization renders correctly.',
        type: 'manual',
      },
    ],
  },

  /* ── 5 — Intelligence System ───────────────────────────────────────── */
  {
    id: 'intelligence',
    number: 5,
    icon: Brain,
    title: 'Intelligence System',
    subtitle: 'Intelligent views for Shows, Music, and Money — insights from account data',
    status: 'not-built',
    statusBanner:
      'No intelligence layer exists. The Activity system provides a feed of events but no analysis or insights. The plan calls for an "Intelligent View" toggle inside Shows, Music, and Money that surfaces signals like: shows at risk, slow ticket pacing, market comparisons, cash flow warnings, financial projections. Depends on historical data from ticket history, financial transactions, and sync states. Budget: $750 (Richard\'s brother).',
    color: 'rose',
    tasks: [
      {
        title: 'Design intelligence signal definitions and thresholds',
        description:
          'Define what constitutes "at risk", "slow pacing", "cash flow warning". Define the input data each signal requires and the thresholds for triggering. This design doc informs both backend logic and UI.',
        type: 'ai',
      },
      {
        title: 'Build "Intelligent View" toggle component',
        description:
          'Create a reusable component for Shows, Music, and Money pages. When toggled on, it overlays insight cards on the default view. Suggested actions: create a task, notify team, review data.',
        type: 'ai',
      },
      {
        title: 'Implement Shows intelligence: at-risk, pacing analysis',
        description:
          'Using ticket history data: calculate current vs expected pacing, flag shows at risk. Display historical performance in that market. Requires Ticket History (Section 4) to be built first.',
        type: 'ai',
      },
      {
        title: 'Implement Money intelligence: cash flow warnings, projections',
        description:
          'Analyze upcoming transactions: cash flow risk (expenses > income), unpaid invoices, commissions owed, high expenses. Example: "Expenses exceed projected income in the next 30 days."',
        type: 'ai',
      },
      {
        title: 'Implement Music intelligence: streaming trends',
        description:
          'Using streaming data from Soundcharts: declining streams, underperforming releases, upcoming releases needing promotion.',
        type: 'ai',
      },
      {
        title: 'Review intelligence signals with real data',
        description:
          'Validate that signals make sense, pacing calculations are correct, cash flow projections are accurate. Tune thresholds.',
        type: 'manual',
      },
    ],
    decisions: [
      {
        title: 'Budget scope: what does $750 cover?',
        description:
          'Is the $750 for backend logic only, full-stack, or just the UI? If backend only, AI can build the frontend.',
      },
      {
        title: 'Client-side or server-side intelligence?',
        description:
          'Compute signals on the frontend (using loaded data) or backend (pre-computed insights via API)? Client-side ships faster; server-side scales better.',
      },
    ],
  },

  /* ── 6 — Production Architecture (EDDIE) ───────────────────────────── */
  {
    id: 'production',
    number: 6,
    icon: Server,
    title: 'Production Architecture',
    subtitle: 'Staging environment, deployment pipeline, environment separation',
    status: 'not-built',
    statusBanner:
      'No staging environment exists. The app runs in a single environment. No deployment pipeline (Dev → Staging → QA → Production). This is infrastructure work. Deployment SOPs, rollback, and verification live in §17. Broader API readiness and observability are in §16 — treat those as the production spine alongside this section.',
    color: 'rose',
    tasks: [
      {
        title: 'Set up staging environment (separate app + database)',
        description:
          'Create a staging deployment with its own database. Structure: production app + production database, staging app + staging database.',
        type: 'manual',
      },
      {
        title: 'Configure deployment pipeline: Dev → Staging → QA → Production',
        description:
          'Set up CI/CD so staging branch auto-deploys, and production requires manual promotion after QA. GitHub Actions, Vercel previews, or similar.',
        type: 'manual',
      },
      {
        title: 'Add environment variable separation',
        description:
          'Ensure .env.staging and .env.production are properly configured with separate API URLs, database connections, and feature flags.',
        type: 'manual',
      },
      {
        title: 'Add environment-aware config utility',
        description:
          'Create a utility that reads VITE_ENV to determine the current environment. Useful for staging banners, feature flags, and API pointing.',
        type: 'ai',
      },
    ],
  },

  /* ── 7 — GA4 Analytics (EDDIE) ─────────────────────────────────────── */
  {
    id: 'analytics',
    number: 7,
    icon: BarChart3,
    title: 'GA4 Analytics',
    subtitle: 'Google Analytics on top of existing custom analytics tracking',
    status: 'partially-built',
    statusBanner:
      'The backend already has custom analytics tracking: session start/end/heartbeat, app usage, page views, interactions, scroll depth (analyticsTracking routes + AnalyticsController). Admin-only daily analytics reports exist. HOWEVER: no GA4/gtag.js in the frontend. The plan calls for GA4 specifically for: signups, invites, shows created, ticket updates, link creation, link clicks, money entries. GA4 would complement the existing custom tracking with Google\'s dashboard, audiences, and conversion tracking.',
    color: 'amber',
    tasks: [
      {
        title: 'Set up GA4 property and get measurement ID',
        description:
          'Create a GA4 property in Google Analytics. Get the measurement ID (G-XXXXXXXXXX). Decide: one property for staging + production or separate?',
        type: 'manual',
      },
      {
        title: 'Install GA4 gtag.js and create analytics utility',
        description:
          'Add the GA4 script to index.html. Create src/lib/analytics.ts with typed helpers: trackSignup(), trackInviteSent(), trackShowCreated(), trackTicketUpdate(), trackLinkCreated(), trackLinkClick(), trackMoneyEntry(). These complement the existing custom tracking.',
        type: 'ai',
      },
      {
        title: 'Wire GA4 events into existing flows',
        description:
          'Many flows already have logActivity() calls. Add GA4 tracking alongside them at key points: signup, invite, show CRUD, ticket counter, link creation, link page interactions, Money add/edit.',
        type: 'ai',
      },
      {
        title: 'Verify events in GA4 real-time dashboard',
        description:
          'Perform key actions and verify events appear in GA4 real-time. Confirm event names and parameters.',
        type: 'manual',
      },
    ],
  },

  /* ── 8 — Admin Dashboard (EDDIE) ──────────────────────────────────── */
  {
    id: 'admin',
    number: 8,
    icon: ShieldCheck,
    title: 'Secure Internal Admin Dashboard',
    subtitle: 'Frontend admin panel powered by existing backend admin endpoints',
    status: 'partially-built',
    statusBanner:
      'Backend admin routes already exist: /api/v1/admin (db-seed, login, me, getUsers). The adminOnly middleware gates analytics endpoints. HOWEVER: there is no admin frontend — no /admin page in the React app. The plan requires: view users/accounts, inspect show sync states, view ticket pacing history, view financial entries, debug account issues. Security: admin role, email whitelist, 2FA. §16 expands this into full admin hardening, usage/rate-limit visibility, and operational tooling.',
    color: 'amber',
    tasks: [
      {
        title: 'Build admin page shell with auth gate',
        description:
          'Create /admin route with AdminPage component. Use the existing backend admin/login endpoint for authentication. Add admin role check and email whitelist.',
        type: 'ai',
      },
      {
        title: 'Build admin views: users, shows, financials, sync states',
        description:
          'Add admin views to browse: all users (with search), all shows (with sync state), ticket pacing history, financial entries, team relationships. Read-only inspection with search/filter. Backend getUsers endpoint exists; may need additional admin endpoints for shows/financials.',
        type: 'ai',
      },
      {
        title: 'Add additional admin API endpoints',
        description:
          'Extend backend-dizee /api/v1/admin with: list all shows (across accounts), list all financial entries, get sync state for a show, get team members for a user. These power the admin views.',
        type: 'ai',
      },
      {
        title: 'Set up admin email whitelist and 2FA',
        description:
          'Define the admin email whitelist. Implement 2FA for admin users — TOTP (Google Authenticator) or email OTP.',
        type: 'manual',
      },
    ],
  },

  /* ── 9 — System Monitoring (EDDIE) ────────────────────────────────── */
  {
    id: 'monitoring',
    number: 9,
    icon: Activity,
    title: 'System Monitoring',
    subtitle: 'Error logging, API logs, failed request alerts',
    status: 'not-built',
    statusBanner:
      'No system monitoring exists. Need: runtime error logging, backend job monitoring, API request/response logs, failed request alerts. §16 defines production-grade health monitoring, dashboards, and alerting across APIs, jobs, imports, email ingestion, and AI — use that as the full scope; this section remains the checklist for baseline tooling.',
    color: 'rose',
    tasks: [
      {
        title: 'Set up error tracking (Sentry or similar)',
        description:
          'Set up Sentry for frontend and backend. Configure source maps. Set up alert rules.',
        type: 'manual',
      },
      {
        title: 'Add frontend error boundary with reporting',
        description:
          'Create a React error boundary that catches rendering errors, reports to monitoring service, and shows a user-friendly error screen.',
        type: 'ai',
      },
      {
        title: 'Add API request logging middleware',
        description:
          'Add Express middleware to log: method, path, status code, response time, errors. Structured log format.',
        type: 'manual',
      },
      {
        title: 'Set up failed request alerts',
        description:
          'Configure alerts (Slack, email) for: 5xx spikes, slow APIs, failed background jobs.',
        type: 'manual',
      },
    ],
  },

  /* ── 10 — Data Architecture for Intelligence ───────────────────────── */
  {
    id: 'data-architecture',
    number: 10,
    icon: Database,
    title: 'Data Architecture for Intelligence',
    subtitle: 'Historical records that power the intelligence layer',
    status: 'partially-built',
    statusBanner:
      'The Activity model stores event history (eventType, section, actor, target, status, timestamps). This covers activity-level audit trail. GAPS: No dedicated ticket count history (covered in Section 4). No financial transaction change log (edits, status changes). No link usage event records (individual clicks/pre-saves). These datasets are needed for the intelligence layer.',
    color: 'amber',
    tasks: [
      {
        title: 'Build financial transaction change log',
        description:
          'Store historical snapshots when financial entries are edited or change status. Allows the intelligence layer to analyze trends and detect anomalies.',
        type: 'ai',
      },
      {
        title: 'Build link usage event tracking',
        description:
          'Store individual link interaction events: clicks, pre-saves, form submissions, timestamps, referrer data. Backend-side — not just aggregate counts.',
        type: 'ai',
      },
      {
        title: 'Set up database indexes and retention policies',
        description:
          'Ensure historical data tables have proper indexes for intelligence query patterns. Define retention policies.',
        type: 'manual',
      },
    ],
  },

  /* ── 11 — QA: Activity, Team Experience & Granular Permissions ─────── */
  {
    id: 'qa-permissions',
    number: 11,
    icon: ClipboardCheck,
    title: 'QA: Activity, Team Experience & Granular Permissions',
    subtitle: 'End-to-end testing and fixing gaps in the consolidated team experience and permission enforcement',
    status: 'exists-needs-work',
    statusBanner:
      'The consolidated team experience, activity system, and granular permissions are all built — but the audit found real gaps. Backend does NOT enforce regionFilter, dateFilter, or showFilter on bookings (frontend-only gating — direct API calls bypass). financialAccess per section is defined in sectionConfig but TeamMember.financialAccess is a single boolean that doesn\'t sync from invite config. OverviewPage and MusicPage don\'t check isFinancialHidden. SchedulePage imports useUserPermissions but doesn\'t use isViewOnly. Activity and Chat have no ProtectedRoute. Bookings routes don\'t use checkFinancialAccess or filter financial fields.',
    color: 'amber',
    tasks: [
      {
        title: 'Fix backend: enforce regionFilter/dateFilter/showFilter on bookings API',
        description:
          'Backend getBookings returns ALL bookings for a user. When the requesting user is a team member, apply their sectionConfig shows filters (regionFilter, dateFilter, showFilter) server-side. This prevents filter bypass via direct API calls. Currently only the frontend filters.',
        type: 'ai',
      },
      {
        title: 'Fix backend: sync financialAccess from sectionConfig on invite accept',
        description:
          'When a team member accepts an invitation, TeamMember.financialAccess is set based on accountType (manager=true), ignoring the per-section financialAccess in sectionConfig. Fix the accept flow to derive financialAccess from the actual permission config sent during invite.',
        type: 'ai',
      },
      {
        title: 'Fix backend: add financial field filtering on bookings routes',
        description:
          'Bookings routes don\'t use checkFinancialAccess or filterFinancialData. When a team member without financial access fetches bookings, financial fields (guarantee, walkout_potential, gross_potential, net_potential, etc.) should be stripped server-side.',
        type: 'ai',
      },
      {
        title: 'Fix frontend: add isFinancialHidden to OverviewPage and MusicPage',
        description:
          'OverviewPage shows financial metrics without checking isFinancialHidden("overview"). MusicPage shows streams/royalty data without checking isFinancialHidden("music"). Add these checks so team members without financial access don\'t see financial data.',
        type: 'ai',
      },
      {
        title: 'Fix frontend: enforce isViewOnly on SchedulePage',
        description:
          'SchedulePage imports useUserPermissions but never calls isViewOnly("schedule"). View-only team members can currently edit schedule items. Add edit gating.',
        type: 'ai',
      },
      {
        title: 'Add ProtectedRoute to Activity and Chat',
        description:
          'Activity page has no ProtectedRoute — it\'s visible to everyone including team members who shouldn\'t see it. Chat also has no route protection. Decide whether these should be gated by section permissions or left open, and implement accordingly.',
        type: 'ai',
      },
      {
        title: 'Fix backend: enforce team action permissions server-side',
        description:
          'canInviteMembers, canRemoveMembers, canEditPermissions, canApproveEdits are only enforced on the frontend. Backend team routes check artist ownership or generic team access but don\'t enforce granular team action permissions. A team member could bypass these via direct API calls.',
        type: 'ai',
      },
      {
        title: 'QA: Test full team invite → accept → permissions flow',
        description:
          'Invite a Manager with full access. Invite a Support with limited sections (shows + tasks only). Invite a Promoter. Accept each. Verify: sidebar shows correct sections, pages enforce view-only vs edit, financial data is hidden when configured, region/date/show filters work, approve/deny flow works. Test with direct API calls to verify backend enforcement.',
        type: 'manual',
      },
      {
        title: 'QA: Test multi-team toggle and filtering',
        description:
          'Log in as a team member on 2+ teams. Toggle artists on/off in Team Hub. Verify: Shows, Music, Tasks, Money all update to show data from active artists only. Filter by specific artist. Add entries per artist. Verify cross-team data isolation.',
        type: 'manual',
      },
      {
        title: 'QA: Test activity approve/deny flow end-to-end',
        description:
          'As a team member, create entries in private/staging visibility on Shows, Music, Tasks, Money. Submit for team visibility. As the artist/admin, approve some and deny others. Verify: approved items become team-visible, denied items stay private, progress bar updates, activity feed reflects all actions.',
        type: 'manual',
      },
      {
        title: 'QA: Test permission editing after invite',
        description:
          'After a team member is active, use Edit Permissions to: add a section, remove a section, change view→edit, toggle financial access. Verify changes take effect immediately on the team member\'s next page load. Verify backend enforces the updated permissions.',
        type: 'manual',
      },
    ],
  },

  /* ── 12 — Tutorial & Onboarding Flow ───────────────────────────────── */
  {
    id: 'tutorial',
    number: 12,
    icon: GraduationCap,
    title: 'Tutorial & Onboarding Flow',
    subtitle: 'First-time user experience, guided tour, empty states, and getting-started flow',
    status: 'not-built',
    statusBanner:
      'The new app has NO tutorial or onboarding guide. The legacy app had a full interactive tour system (interactiveTour.js with TOUR_SECTIONS, FloatingTourChat, InteractiveTour, per-section welcome messages and Q&A) — none of this was ported to the new React app. Post-signup, artists land on /shows with no guidance. Team members land on /team. Empty states exist on some pages (Shows, TeamHub) but are minimal. There is no "Getting Started" checklist, no tooltip tour, and no contextual help. The signup flow itself is solid (8 steps for artists, team member path) but what happens after signup needs work.',
    color: 'rose',
    tasks: [
      {
        title: 'Design onboarding checklist for new artists',
        description:
          'After signup, show a "Getting Started" checklist that guides users through: adding their first show, importing catalog from Spotify, inviting a team member, exploring the schedule, and setting up settings. Track completion in localStorage or backend. Dismiss after all items complete.',
        type: 'ai',
      },
      {
        title: 'Build tooltip tour component (reusable)',
        description:
          'Create a lightweight tooltip tour system that highlights specific UI elements with popovers. Steps can be defined per page. Should support: highlight a DOM element, show description, next/skip/done actions. Can reference the legacy interactiveTour.js content for copy.',
        type: 'ai',
      },
      {
        title: 'Build Shows page first-time tour',
        description:
          'When a user first visits /shows (or has zero shows), show a guided tour: how to add a show manually, how to import from CSV, how to sync from Bandsintown, how the ticket counter works. Only show once (persist dismissal).',
        type: 'ai',
      },
      {
        title: 'Build team member onboarding experience',
        description:
          'When a team member first logs in after accepting an invite, show a contextual welcome: "You\'re now part of [Artist]\'s team as a [Role]." Explain what they can access based on their permissions. Guide them to toggle accounts on/off in Team Hub.',
        type: 'ai',
      },
      {
        title: 'Improve empty states across all pages',
        description:
          'Review Shows, Music, Tasks, Money, Links, Schedule, and Activity for empty-state quality. Each should: explain what the section does, provide a clear CTA to add the first item, and link to help/knowledge if available. Currently most are minimal "No data" messages.',
        type: 'ai',
      },
      {
        title: 'Port legacy tour content for reference',
        description:
          'The legacy interactiveTour.js has comprehensive section descriptions, welcome messages, and Q&A content for every section. Extract and adapt this content for the new app\'s tour system rather than writing from scratch.',
        type: 'ai',
      },
      {
        title: 'Test onboarding with a fresh account',
        description:
          'Create a fresh artist account and a fresh team member account. Walk through the entire post-signup experience. Verify the checklist appears, tours trigger at the right time, empty states are helpful, and the experience feels smooth for a first-time user.',
        type: 'manual',
      },
    ],
  },

  /* ── 13 — Customer Journey & Lifecycle Emails ──────────────────────── */
  {
    id: 'emails',
    number: 13,
    icon: Mail,
    title: 'Customer Journey & Lifecycle Emails',
    subtitle: 'Welcome emails, onboarding sequences, notifications, and lifecycle touchpoints',
    status: 'partially-built',
    statusBanner:
      'Existing emails: OTP verification, team invite (Mailgun HTML), artist invite to Dizee (with signup link), team join/leave notifications, delegate access, admin emails (waitlist, feedback, daily analytics). GAPS: No welcome email after signup. No onboarding email sequence. No promoter invite email (PromoterRequest creates a record but sends no email). No daily/weekly digest email to users. No re-engagement emails. No email when important events happen (show created, ticket milestone, financial update). The team invite email currently shows the beta password in the body and says "Visit diz.ee" with no direct accept link.',
    color: 'amber',
    tasks: [
      {
        title: 'Build welcome email after signup',
        description:
          'Send a welcome email immediately after account creation (auth/add_user_info_personal for artists, auth/add_user_info for team members). Include: welcome message, quick tips for getting started, link to the app. Use the existing Mailgun infrastructure.',
        type: 'ai',
      },
      {
        title: 'Build onboarding email sequence (3-5 emails)',
        description:
          'After signup, send a drip sequence over 7-14 days: Day 1 — welcome + getting started, Day 3 — "Add your first show", Day 5 — "Invite your team", Day 7 — "Explore your data", Day 14 — "How\'s it going?" feedback request. Track which emails have been sent per user. Needs a simple email queue or cron-based sender.',
        type: 'ai',
      },
      {
        title: 'Add email to promoter invite flow',
        description:
          'POST promoter/invite-artist creates a PromoterRequest but sends NO email. Add a Mailgun email to the artist inviting them to connect with the promoter on DIZEE. Include promoter name, show names, and a direct link.',
        type: 'ai',
      },
      {
        title: 'Improve team invite email UX',
        description:
          'Current team invite email shows the beta password and says "Visit diz.ee" with no direct link. Replace with: a direct accept link (using the inviteToken), clear role description, and what they\'ll be able to do. Remove beta password from email body once out of beta.',
        type: 'ai',
      },
      {
        title: 'Build event notification emails (opt-in)',
        description:
          'Send emails when significant events happen: new show added to your account, ticket milestone reached (50%, 75%, sold out), financial entry past due, team member joined. These should be opt-in via Settings. Build a notification preferences system.',
        type: 'ai',
      },
      {
        title: 'Build weekly digest email for users',
        description:
          'Weekly summary email: upcoming shows this week, recent financial activity, team activity summary, any intelligence alerts. Use the existing daily digest logic (dailyDigest.ts) as a foundation but send via email instead of just in-app.',
        type: 'ai',
      },
      {
        title: 'Design and build email templates',
        description:
          'Create a consistent email template system for all DIZEE emails. Should match the DIZEE brand (dark, clean, minimal). Reusable header/footer, consistent CTA button styling. Replace the inline HTML currently in team.ts with template functions.',
        type: 'ai',
      },
      {
        title: 'Set up email deliverability and test all emails',
        description:
          'Verify Mailgun domain authentication (SPF, DKIM, DMARC). Test every email type: welcome, onboarding sequence, team invite, promoter invite, event notifications, weekly digest. Check spam score. Verify links work.',
        type: 'manual',
      },
      {
        title: 'Map the complete customer journey and identify touchpoints',
        description:
          'Create a full customer journey map from first hearing about DIZEE through onboarding, daily use, team collaboration, and growth. Identify every touchpoint where an email or in-app notification should exist. Verify nothing is missing.',
        type: 'manual',
      },
    ],
    decisions: [
      {
        title: 'Email frequency and opt-out strategy',
        description:
          'How aggressive should the email cadence be? Should all non-transactional emails be opt-in by default? Should there be a global email preferences page in Settings? This affects the notification preferences system design.',
      },
      {
        title: 'Beta password in emails',
        description:
          'Team invite emails currently include the beta password "betadizee101" in the body. When should this be removed? Should it be replaced with a direct-access link that bypasses the protect gate?',
      },
    ],
  },

  /* ── 14 — Platform rate limiting & usage protection ─────────────────── */
  {
    id: 'rate-limits',
    number: 14,
    icon: Gauge,
    title: 'Platform Rate Limiting & Usage Protection',
    subtitle: 'Centralized quotas, cost control, abuse prevention across Dizee',
    status: 'not-built',
    statusBanner:
      'No unified rate-limit and quota layer exists today. This workstream is a core production deliverable: one reusable framework (prefer Redis or equivalent shared store) for distributed limits across API routes, AI, uploads, Soundcharts imports, Mailgun flows, and future integrations — not one-off checks per endpoint.',
    color: 'rose',
    purpose:
      'Protect Dizee from abuse, runaway third-party costs, storage/bandwidth creep, and traffic spikes while laying groundwork for plan-based quotas and monetization.',
    implementationConsiderations: [
      'Hard vs soft caps by feature; consistent error shapes for the frontend.',
      'Limits must behave correctly across multiple app instances/workers (shared store + atomic operations).',
      'Structured logging + metrics for spikes; admin surfaces for events, blocks, and top offenders.',
      'Config via environment variables and admin settings for future tiers.',
    ],
    dependencies: [
      'Redis or chosen shared store provisioned for production/staging.',
      'Inventory of cost-driving routes and background jobs (AI, Mailgun, Soundcharts, uploads, queues).',
      'Product agreement on default caps (below) before launch.',
    ],
    roadmapPlacement:
      'Start early in the month in parallel with permission fixes: wire the framework first, then attach AI/upload/Soundcharts/Mailgun policies. Frontend limit UX and admin views follow once counters are trustworthy.',
    doneCriteria: [
      'Central framework in use across targeted routes with tests for multi-instance behavior.',
      'AI daily cap: 10,000 tokens per user (input + output tracked), reset on calendar or rolling day, configurable.',
      'AI features degrade or block cleanly with clear UI: remaining allowance, limit reached, next reset.',
      'Image uploads: per-user limits, size/type validation, frequency caps, logging of attempts and rejects.',
      'Soundcharts: 10 single imports and 10 bulk imports per user per day (separate counters), dedupe/waste reduction where possible, user errors + admin usage/failure views.',
      'Mailgun: rate limits and daily caps by action type (invites, resets, magic links, campaigns, etc.), send volume logging.',
      'Other cost drivers mapped and attached to the same system where material.',
      'Alerting on abnormal patterns; admins can investigate usage and offenders.',
    ],
    tasks: [
      {
        title: 'Design and implement centralized rate-limit / quota service',
        description:
          'Shared store (Redis preferred), keying strategy for user / IP / endpoint / workspace / plan (future). Support sliding and fixed windows. Atomic increment + TTL. Pluggable middleware for Express and hooks for workers.',
        type: 'ai',
      },
      {
        title: 'Apply global and per-route request limits',
        description:
          'Roll out request-level limits across high-traffic and sensitive API routes; stricter buckets for expensive or abuse-prone handlers.',
        type: 'ai',
      },
      {
        title: 'AI token accounting and daily cap (10k/user/day)',
        description:
          'Track input and output tokens per call; persist daily aggregates per user; reset on calendar day or rolling 24h (configurable). Block or gracefully degrade AI features at limit. Wire limits from env/admin.',
        type: 'ai',
      },
      {
        title: 'AI usage UX in product',
        description:
          'Show remaining allowance, limit reached state, and next reset where relevant (chat, settings, or inline). Frontend-safe error payloads when throttled.',
        type: 'ai',
      },
      {
        title: 'Image upload protections',
        description:
          'Per-user upload quotas, max file size, MIME/extension validation, frequency caps, spam prevention. Log allowed, rejected, and suspicious attempts for audit.',
        type: 'ai',
      },
      {
        title: 'Soundcharts import limits and visibility',
        description:
          'Enforce 10 single and 10 bulk imports per user per day (separate counters). Reduce duplicate or wasteful jobs. User-facing errors when exceeded. Admin dashboard: usage and failures.',
        type: 'ai',
      },
      {
        title: 'Mailgun / outbound email protections',
        description:
          'Rate limit and cap all Mailgun-powered flows (invites, notifications, password reset, magic links, onboarding, campaigns). Per-action caps; log send volume and rejected attempts.',
        type: 'ai',
      },
      {
        title: 'Cost-driver inventory and coverage',
        description:
          'Document and attach limits to scraping, heavy API use, background jobs, queue floods, storage-heavy paths, and new integrations — extend framework instead of ad hoc checks.',
        type: 'manual',
      },
      {
        title: 'Admin: rate events, blocks, and top usage',
        description:
          'Views or exports for limit hits, blocked actions, and heaviest users/workspaces; tie into existing admin auth.',
        type: 'ai',
      },
      {
        title: 'Observability: metrics, logs, alerts',
        description:
          'Structured logging for limit decisions; dashboards or alerts for spikes, sustained abuse, and integration failures.',
        type: 'manual',
      },
    ],
  },

  /* ── 15 — AI context, memory & backend hardening ───────────────────── */
  {
    id: 'ai-context',
    number: 15,
    icon: Layers,
    title: 'AI Context Architecture & Memory',
    subtitle: 'Permission-safe, retrievable context — not bolt-on chat only',
    status: 'not-built',
    statusBanner:
      'AI features need a first-class context layer: separated personal, workspace, artist/project, and platform scopes; persistent memory with lifecycle rules; assembly pipelines that rank, permission-check, and trim before model calls. This underpins every future AI surface.',
    color: 'violet',
    purpose:
      'Make the backend explicitly AI-native and context-ready so experiences strengthen per user/account over time without cross-tenant leakage.',
    implementationConsiderations: [
      'Embeddings, summaries, and structured stores where raw chat history is insufficient.',
      'Rules for what may be stored, updated, compacted, archived, or invalidated.',
      'Debuggability: trace which sources influenced a response (privacy-preserving).',
    ],
    dependencies: [
      'Solid auth and workspace/team models (existing).',
      'Rate limits and token accounting (§14) for sustainable AI usage.',
      'Claude-first routing (§16) for consistent provider behavior.',
    ],
    roadmapPlacement:
      'After initial quota framework: define schemas and retrieval contracts, then wire chat and other AI entry points. Settings and controls ship with the memory layer so users are not surprised.',
    doneCriteria: [
      'Context segmented by user / artist / workspace with enforced permission checks on retrieval.',
      'Persistent memory with short- and long-term patterns; compaction and invalidation paths exist.',
      'Assembly pipeline pulls from history, workspace data, catalog, tasks, CRM, imports, events, saved memory, and current UI entity.',
      'User/admin settings: enable memory, categories allowed, workspace vs personal scope, clear/reset, visibility into summaries where appropriate.',
      'Observability for retrieval quality; guards against stale or toxic memory.',
    ],
    tasks: [
      {
        title: 'Model context domains and storage',
        description:
          'Define schemas for personal vs workspace vs artist vs global platform context. Ensure retrieval APIs never cross permission boundaries.',
        type: 'ai',
      },
      {
        title: 'Persistent memory layer',
        description:
          'Store durable facts, preferences, workflows, and decisions; session vs long-term; rules for save/no-save; update, summarize, compact, archive.',
        type: 'ai',
      },
      {
        title: 'Context assembly pipeline',
        description:
          'Composable pipeline: fetch candidates, rank, permission-filter, trim to token budget, attach to prompts/tools. Reusable across chat, agents, and batch features.',
        type: 'ai',
      },
      {
        title: 'Structured data for retrieval',
        description:
          'Canonical summaries, metadata tags, embeddings/indexes as needed; reduce fragmented duplicate context across services.',
        type: 'ai',
      },
      {
        title: 'AI memory & context settings',
        description:
          'Settings UI: toggles, category controls, scope, clear memory, optional visibility into saved summaries. Production-safe defaults.',
        type: 'ai',
      },
      {
        title: 'Quality and evaluation hooks',
        description:
          'Logging for retrieved chunks; staleness/relevance signals; foundation for future evals and regression tests on context quality.',
        type: 'ai',
      },
    ],
  },

  /* ── 16 — API, email ingestion, Claude, observability, admin, usage ─── */
  {
    id: 'platform-ops',
    number: 16,
    icon: Network,
    title: 'API, Email Ingestion, Claude-First AI, Observability & Admin',
    subtitle: 'External readiness, inbound email, provider migration, health, admin UX',
    status: 'not-built',
    statusBanner:
      'Consolidates API standardization, CC/forward email ingestion, switching default AI to Claude, production monitoring/alerting, hardened admin, and usage surfaces in settings. Overlaps with prior §6 staging, §8 admin UI, and §9 monitoring — those sections stay as tactical checklists; this is the full production scope.',
    color: 'cyan',
    purpose:
      'Make Dizee operationally complete: integratable API, email-to-platform workflows, Claude-first models, real observability, and admin/tools that match how the product will run at scale.',
    implementationConsiderations: [
      'Versioned API patterns, consistent errors, auth modes for future partners.',
      'Inbound email must be permission-safe, deduped, and tolerant of malformed mail.',
      'Claude migration: router abstraction, prompt/tool parity, token accounting updates.',
    ],
    dependencies: [
      'Mailgun (existing) for outbound; inbound may need routes, parsing service, or provider features.',
      'Rate limits (§14) and AI context (§15) aligned with new defaults.',
    ],
    roadmapPlacement:
      'API standards and health monitoring can start immediately. Claude cutover after parity checklist. Email ingestion is a parallel track with clear MVP (receive, parse, attach) then threading/AI analysis. Admin + usage UI land after core metrics exist.',
    doneCriteria: [
      'Documented internal/public API surface with auth, validation, and error contracts.',
      'Users can CC/forward into Dizee with parsing, entity association, metadata storage, and fallbacks for unmatched mail.',
      'Claude is default provider with configurable routing; all AI surfaces verified.',
      'Dashboards/alerts for health: infra, API, jobs, queues, imports, email, AI, third parties.',
      'Admin area role-based, complete for users, permissions, imports, AI usage, email, limits, events.',
      'Settings show usage: AI, imports, uploads, email-related actions, metered features — current, remaining, reset.',
    ],
    tasks: [
      {
        title: 'API layer standardization and expansion',
        description:
          'Consistent validation, auth, permissions, pagination, error format. Identify core entities (artists, shows, music, links, tasks, notes, imports, AI actions) for current and near-term API access. Design for partners and automation.',
        type: 'ai',
      },
      {
        title: 'Inbound email ingestion (CC / forward)',
        description:
          'Receive pipeline, parse headers/body/attachments, map to user/artist/workspace/show/release/task/CRM; thread metadata; dedupe; spam/malformed handling; audit trail. Fallback rules for ambiguous mail. Roadmap: threading, timeline, AI summaries.',
        type: 'ai',
      },
      {
        title: 'Claude-first migration',
        description:
          'Default provider = Claude; refactor router, env config, prompts, tools, streaming, structured outputs. Preserve ability to swap models/providers. Update usage and rate-limit accounting for Claude tokenization.',
        type: 'ai',
      },
      {
        title: 'System health monitoring and alerting',
        description:
          'Coverage: infra, APIs, background jobs, queues, cron, imports, email ingest, AI calls, external APIs. Dashboards + paging for failures, degradation, spikes, provider outages, repeated job failures.',
        type: 'manual',
      },
      {
        title: 'Admin hardening and completeness',
        description:
          'Secure, RBAC-separated admin UX: users, accounts, permissions, imports, AI usage, ingestion, rate limits, system events, operational tools. Auditability and safety rails on destructive actions.',
        type: 'ai',
      },
      {
        title: 'Usage visibility in settings',
        description:
          'Surfaces for AI, imports, uploads, email sends, and other metered actions — usage, remaining, reset time, blocked state. Admin aggregates where appropriate. Ready for future billing tiers.',
        type: 'ai',
      },
      {
        title: 'Staging + deployment pipeline (with §17 SOPs)',
        description:
          'Separate staging/prod apps and databases; CI/CD with promotion gates; env separation — coordinated with deployment SOPs.',
        type: 'manual',
      },
      {
        title: 'Baseline error tracking and API logging',
        description:
          'Sentry (or equivalent) front/back, structured API logs, error boundaries — aligns with §9 checklist.',
        type: 'manual',
      },
    ],
  },

  /* ── 17 — Operational SOPs ──────────────────────────────────────────── */
  {
    id: 'sops',
    number: 17,
    icon: BookOpen,
    title: 'Deployment, Maintenance & Operations SOPs',
    subtitle: 'Repeatable procedures — part of production readiness',
    status: 'not-built',
    statusBanner:
      'Operational documentation is a deliverable: deployment, maintenance, incidents, releases, admin usage, and access/security practices. Centralized, maintainable, usable without tribal knowledge.',
    color: 'amber',
    purpose:
      'Scale team and uptime safely: everyone can deploy, maintain, and respond using the same playbooks.',
    implementationConsiderations: [
      'Single source of truth location (repo docs or internal wiki) linked from onboarding.',
      'SOPs updated when architecture changes (rate limits, email ingest, Claude, etc.).',
    ],
    dependencies: [
      'Staging and pipeline existence (§6 / §16).',
      'Monitoring and alerting (§16) to make runbooks actionable.',
    ],
    roadmapPlacement:
      'Draft core SOPs as soon as staging exists; refine after first production deploy. Incident and admin SOPs evolve with real events.',
    doneCriteria: [
      'Deployment: pre-checks, steps, verification, rollback, post-deploy monitoring; emergency hotfix path.',
      'Maintenance: daily/weekly/monthly checks across app, DB, jobs, email, AI, imports, storage, admin.',
      'Incident response: runbooks for outages, degraded perf, AI/provider failures, imports, email parse, queues, DB, third parties.',
      'Release management: versioning, QA, approvals, changelog, comms.',
      'Admin/ops: how to use admin safely; escalation for risky actions.',
      'Access/security: env vars, keys, admin roles, revocation, least privilege.',
    ],
    tasks: [
      {
        title: 'Write deployment SOPs (dev / staging / prod)',
        description:
          'Pre-deploy checklist, deploy steps, smoke tests, rollback, post-deploy monitoring, hotfix path.',
        type: 'manual',
      },
      {
        title: 'Write maintenance SOPs',
        description:
          'Recurring checks: health, failed jobs, stale data, limits, integrations, security, usage anomalies — daily/weekly/monthly cadence.',
        type: 'manual',
      },
      {
        title: 'Incident response and troubleshooting runbooks',
        description:
          'Outages, slow APIs, AI failures, import/email/queue/DB/integration failures — first checks, isolation, recovery.',
        type: 'manual',
      },
      {
        title: 'Release management process',
        description:
          'Feature vs fix releases, QA bar, approvals, versioning, changelog, stakeholder comms.',
        type: 'manual',
      },
      {
        title: 'Admin and operational SOPs',
        description:
          'How operators use admin for monitoring, user help, imports, limits, usage review; guardrails and escalation.',
        type: 'manual',
      },
      {
        title: 'Access and security SOPs',
        description:
          'Secrets handling, key rotation, admin access, role changes, revocation, least privilege.',
        type: 'manual',
      },
    ],
  },

  /* ── 18 — Full-stack codebase refactor (frontend + backend) ─────────── */
  {
    id: 'fullstack-refactor',
    number: 18,
    icon: Boxes,
    title: 'Full-Stack Codebase Refactor & Production Alignment',
    subtitle: 'Harden dizee + backend-dizee in step with SOPs and infra',
    status: 'not-built',
    statusBanner:
      'Beyond feature work, both repositories should be refactored toward the same production bar: clearer module boundaries, consistent patterns, env/config discipline, error and logging contracts, test coverage where it matters, and CI/deploy hooks that match the deployment and maintenance SOPs. This is not cosmetic — it reduces incident surface, speeds onboarding, and keeps frontend and backend changes safe as the team grows.',
    color: 'sky',
    purpose:
      'Align the frontend (dizee) and backend (backend-dizee) codebases with production-ready infrastructure and documented operational procedures so releases, rollbacks, and day-two operations are predictable.',
    implementationConsiderations: [
      'Coordinate refactors with §16 (observability, API shape) and §17 (SOPs) so code changes and runbooks stay in sync.',
      'Prefer incremental refactors with feature flags or thin slices over a big-bang rewrite.',
      'Shared understanding of staging vs prod config, secrets, and health checks.',
    ],
    dependencies: [
      'Staging and basic CI/CD (§6 / §16).',
      'Monitoring and logging baseline (§9 / §16).',
    ],
    roadmapPlacement:
      'Run in parallel with platform phases 6–7: early wins (lint, structure, critical paths) first; deeper module work follows once SOPs and infra contracts are stable.',
    doneCriteria: [
      'Backend: layered structure, consistent middleware and error responses, documented env, integration points for rate limits and observability.',
      'Frontend: consistent data/API layer, error boundaries, env-driven config, patterns for loading and permission-aware UI.',
      'Both: CI checks, deploy artifacts, and on-call/runbook alignment with §17.',
      'Team can deploy and roll back using SOPs without one-off tribal steps.',
    ],
    tasks: [
      {
        title: 'Backend refactor plan and execution (backend-dizee)',
        description:
          'Audit routes, services, and models; reduce duplication; standardize validation, errors, and logging; align with API contracts from §16; ensure workers/cron fit observability and rate-limit hooks.',
        type: 'ai',
      },
      {
        title: 'Frontend refactor plan and execution (dizee)',
        description:
          'Audit pages, stores, and API clients; consolidate patterns; improve error/loading states for production; align with usage and limit UX from §14; tree-shake and bundle hygiene where needed.',
        type: 'ai',
      },
      {
        title: 'Cross-repo alignment with infra and SOPs',
        description:
          'Map environments, build pipelines, and release steps to §17: same naming for stages, health endpoints, and deployment verification. Update docs when structure changes.',
        type: 'manual',
      },
      {
        title: 'Testing and safety nets during refactor',
        description:
          'Add or extend critical-path tests (auth, permissions, payments-adjacent flows, imports) before large moves; avoid regressions during restructuring.',
        type: 'manual',
      },
    ],
  },
]

/* ═══════════════════════════════════════════════════════════════════════════
   EXECUTION ORDER
   ═══════════════════════════════════════════════════════════════════════════ */
interface Phase {
  number: number
  title: string
  subtitle: string
  color: string
  items: string[]
}

const phases: Phase[] = [
  {
    number: 1,
    title: 'Quick Wins + Permission Fixes',
    subtitle: 'One-line changes, unlock features, fix security gaps',
    color: 'emerald',
    items: [
      'Unhide Links in the sidebar (one-line change in Sidebar.tsx)',
      'Fix backend: enforce regionFilter/dateFilter/showFilter on bookings API',
      'Fix backend: sync financialAccess from sectionConfig on invite accept',
      'Fix backend: add financial field filtering on bookings routes',
      'Fix frontend: add isFinancialHidden to OverviewPage and MusicPage',
      'Fix frontend: enforce isViewOnly on SchedulePage',
    ],
  },
  {
    number: 2,
    title: 'Money + Links + QA',
    subtitle: 'Upgrade Money, give Links a backend, QA permissions end-to-end',
    color: 'sky',
    items: [
      'Replace Money summary boxes (Income / Expenses / Projected Net / Cash Flow)',
      'Add time-range filters, status types, attachments, running balance to Money',
      'Add backend persistence for DIZEE Links (replace localStorage)',
      'QA: full team invite → accept → permissions test for all three roles',
      'QA: multi-team toggle and filtering across all sections',
      'QA: activity approve/deny flow end-to-end',
    ],
  },
  {
    number: 3,
    title: 'Show → Money + Ticket History + Emails',
    subtitle: 'Financial pipeline, pacing data, and customer journey',
    color: 'violet',
    items: [
      'Extend showFinancialsService: bonuses, commissions, walkout, deposits',
      'Build payment schedule support (deposit + settlement + bonus payouts)',
      'Create TicketHistory model and wire ticket count updates',
      'Build welcome email + onboarding email sequence (3-5 emails)',
      'Improve team invite email UX (direct accept link, no beta password)',
      'Add email to promoter invite flow',
    ],
  },
  {
    number: 4,
    title: 'Tutorial + Intelligence',
    subtitle: 'First-time user experience and insights layer',
    color: 'amber',
    items: [
      'Build onboarding checklist for new artists',
      'Build tooltip tour component + Shows page first-time tour',
      'Build team member onboarding experience',
      'Improve empty states across all pages',
      'Build "Intelligent View" toggle for Shows, Music, Money',
      'Design and implement intelligence signals (at-risk, pacing, cash flow)',
    ],
  },
  {
    number: 5,
    title: 'Lifecycle Emails + Customer Journey',
    subtitle: 'Event notifications, digest, templates, journey mapping',
    color: 'cyan',
    items: [
      'Build event notification emails (show added, ticket milestone, past due)',
      'Build weekly digest email for users',
      'Design and build consistent email template system',
      'Map complete customer journey and verify all touchpoints',
      'Build notification preferences in Settings (opt-in/opt-out)',
    ],
  },
  {
    number: 6,
    title: 'Platform Foundations (Parallel)',
    subtitle: '§14–15 + start §17 — start early with product phases 1–5',
    color: 'rose',
    items: [
      'Implement centralized rate-limit / quota framework (Redis or equivalent) + global/per-route limits',
      'AI token accounting: 10k/user/day (in+out), daily reset, env/admin config',
      'Image upload + Soundcharts + Mailgun protections wired to the same framework',
      'AI context domains + persistent memory schema + assembly pipeline MVP (permission-safe)',
      'Staging environment + deployment pipeline; draft deployment SOP (§17)',
      'Start full-stack refactor scoping: backend/frontend hotspots, CI gaps, alignment with §17',
    ],
  },
  {
    number: 7,
    title: 'Platform Operations & Integration',
    subtitle: '§16 + complete §14–§17 + GA4, monitoring, admin',
    color: 'violet',
    items: [
      'API standardization and expanded entity coverage; error/auth contracts',
      'Claude-first migration with router abstraction and parity validation across AI surfaces',
      'Inbound email ingestion (CC/forward), parsing, entity mapping, fallbacks',
      'Production observability: dashboards, alerts, job/queue/import/email/AI health',
      'Admin hardening + usage visibility in settings (AI, imports, uploads, email-related)',
      'Operational SOPs: maintenance, incidents, releases, admin ops, access/security',
      'GA4 + Sentry + API logging + deliverability (SPF/DKIM/DMARC) + admin whitelist/2FA',
      'Full-stack refactor (dizee + backend-dizee) aligned with infra and §17 SOPs',
    ],
  },
]

/* ═══════════════════════════════════════════════════════════════════════════
   END-OF-MONTH OUTCOMES
   ═══════════════════════════════════════════════════════════════════════════ */
const outcomes = [
  { label: 'Consolidated experience for all roles (artist, manager, promoter, support)', status: 'complete' as BuildStatus },
  { label: 'Multi-team filtering across Shows, Music, Tasks, Money', status: 'complete' as BuildStatus },
  { label: 'Team Hub with toggle on/off, request access, pending artists', status: 'complete' as BuildStatus },
  { label: 'Activity system with approve/deny and progress tracking', status: 'complete' as BuildStatus },
  { label: 'Show guarantee → Money flow (backend cron + frontend)', status: 'complete' as BuildStatus },
  { label: 'Granular permissions fully enforced (frontend + backend)', status: 'exists-needs-work' as BuildStatus },
  { label: 'DIZEE Links system (visible, backend-persisted)', status: 'exists-needs-work' as BuildStatus },
  { label: 'Money enhancements (summary boxes, filters, statuses, attachments)', status: 'exists-needs-work' as BuildStatus },
  { label: 'Show → Money: bonuses, commissions, payment schedules', status: 'partially-built' as BuildStatus },
  { label: 'Historical ticket pacing data', status: 'not-built' as BuildStatus },
  { label: 'Intelligence layer across Shows, Music, and Money', status: 'not-built' as BuildStatus },
  { label: 'Tutorial & onboarding flow for new users', status: 'not-built' as BuildStatus },
  { label: 'Customer journey emails (welcome, onboarding, notifications, digest)', status: 'partially-built' as BuildStatus },
  { label: 'Centralized rate limits, quotas, and cost protection (AI, uploads, Soundcharts, Mailgun)', status: 'not-built' as BuildStatus },
  { label: 'AI context architecture, persistent memory, assembly pipeline (permission-safe)', status: 'not-built' as BuildStatus },
  { label: 'API readiness, email ingestion, Claude-first AI, observability, admin + usage UX', status: 'not-built' as BuildStatus },
  { label: 'Deployment, maintenance, incident, release, and access SOPs', status: 'not-built' as BuildStatus },
  { label: 'Full-stack codebase refactor aligned with production infra and SOPs (frontend + backend)', status: 'not-built' as BuildStatus },
  { label: 'Production infrastructure (staging, GA4, admin UI, monitoring)', status: 'partially-built' as BuildStatus },
]

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */
export default function PlanAprilPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())
  const [showCompleted, setShowCompleted] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === PLAN_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const expandAll = () => setOpenSections(new Set(sections.map((s) => s.id)))
  const collapseAll = () => setOpenSections(new Set())

  const totalSections = sections.length
  const aiBuildTasks = sections.reduce((sum, s) => sum + s.tasks.filter((t) => t.type === 'ai').length, 0)
  const manualTasks = sections.reduce((sum, s) => sum + s.tasks.filter((t) => t.type === 'manual').length, 0)
  const totalDecisions = sections.reduce((sum, s) => sum + (s.decisions?.length || 0), 0)
  const notBuiltCount = sections.filter((s) => s.status === 'not-built').length

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8 bg-zinc-950">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
              <span className="text-white text-lg font-semibold tracking-tight">DIZEE</span>
              <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-white/10 text-zinc-400 rounded">April Plan</span>
            </div>
            <p className="text-[13px] text-zinc-500">Enter the password to view the April development plan.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-[12px] text-zinc-400 mb-2 tracking-wide uppercase">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Enter password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent transition-all"
              />
              {error && (
                <p className="mt-2 text-[11px] text-rose-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
            >
              View Plan
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.015] rotate-[-25deg]">
          <span className="text-[180px] font-bold tracking-widest text-white whitespace-nowrap select-none">
            CONFIDENTIAL
          </span>
        </div>
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-zinc-950/80 border-b border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
            <span className="text-white text-base font-semibold tracking-tight">DIZEE</span>
            <span className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-white/[0.08] text-zinc-300 rounded-full">
              April Plan
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-zinc-600 uppercase tracking-wider">Confidential</span>
            <Lock className="w-3.5 h-3.5 text-zinc-600" />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-10 space-y-12">
        <div>
          <h1 className="text-2xl font-bold text-white mb-3">April Development Plan</h1>
          <p className="text-[13px] text-zinc-400 leading-relaxed max-w-3xl">
            Audited against both frontend (dizee) and backend (backend-dizee) codebases as of April 2026. The promoter/manager dashboard direction has changed — everything is now consolidated into one DIZEE experience with permission-based filtering. This plan reflects what remains to be built and explicitly treats production readiness — rate limits, AI context architecture, API/email/Claude/observability/admin, and operational SOPs — as core deliverables alongside product features.
          </p>
          <p className="text-[13px] text-zinc-400 leading-relaxed max-w-3xl mt-3">
            We also need a deliberate refactor of both codebases so they align with production-grade infrastructure and the operational SOPs in §17: structure, boundaries, error handling, configuration, testing, and deployability should be brought up together on frontend and backend — not as one-off fixes but as a coordinated hardening pass alongside staging, monitoring, and release discipline.
          </p>
        </div>

        {/* ── Already Completed (Collapsible) ─────────────────────────────── */}
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] overflow-hidden">
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            className="w-full flex items-center gap-4 px-6 py-5 text-left group"
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-emerald-500/15 text-emerald-300">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h2 className="text-[15px] font-semibold text-white">Already Completed ({completedWork.length} items)</h2>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Done
                </span>
              </div>
              <p className="text-[12px] text-zinc-500 mt-1">
                Consolidated experience, multi-team filtering, activity system, show → Money flow, analytics tracking, admin endpoints, sync status, team architecture
              </p>
            </div>
            <div className="flex-shrink-0 text-zinc-600 group-hover:text-zinc-400 transition-colors">
              {showCompleted ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </div>
          </button>

          {showCompleted && (
            <div className="px-6 pb-6 space-y-4">
              {completedWork.map((item, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-emerald-500/10 bg-emerald-500/[0.03] p-4"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <h4 className="text-[13px] font-medium text-white">{item.title}</h4>
                  </div>
                  <p className="text-[12px] text-zinc-500 leading-relaxed mb-3 ml-6">{item.description}</p>
                  <div className="ml-6 space-y-1">
                    {item.files.map((file, j) => (
                      <div key={j} className="text-[11px] text-zinc-600 font-mono flex items-start gap-2">
                        <span className="text-emerald-500/60 mt-0.5">-</span>
                        <span>{file}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Quick Nav Pills ──────────────────────────────────────────────── */}
        <div>
          <h2 className="text-[12px] text-zinc-500 uppercase tracking-wider font-semibold mb-3">Remaining Work</h2>
          <nav className="flex flex-wrap gap-2">
            {sections.map((s) => {
              const c = colorMap[s.color] || colorMap.sky
              return (
                <button
                  key={s.id}
                  onClick={() => {
                    if (!openSections.has(s.id)) toggleSection(s.id)
                    document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-medium border transition-all hover:scale-[1.03] ${c.border} ${c.bg} ${c.text}`}
                >
                  {s.number}. {s.title}
                </button>
              )
            })}
          </nav>
        </div>

        {/* ── Progress Overview Cards ──────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Remaining Sections', value: totalSections, color: 'text-white' },
            { label: 'AI Tasks', value: aiBuildTasks, color: 'text-sky-400' },
            { label: 'Eddie Tasks', value: manualTasks, color: 'text-violet-400' },
            { label: 'Decisions Needed', value: totalDecisions, color: 'text-amber-400' },
            { label: 'Not Yet Built', value: notBuiltCount, color: 'text-rose-400' },
          ].map((card) => (
            <div
              key={card.label}
              className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5"
            >
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider mb-2">{card.label}</div>
              <div className={`text-2xl font-semibold ${card.color}`}>{card.value}</div>
            </div>
          ))}
        </div>

        {/* ── Expand / Collapse All ────────────────────────────────────────── */}
        <div className="flex items-center gap-3">
          <button
            onClick={expandAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Expand All
          </button>
          <span className="text-zinc-700">|</span>
          <button
            onClick={collapseAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Collapse All
          </button>
        </div>

        {/* ── Sections ─────────────────────────────────────────────────────── */}
        <div className="space-y-4">
          {sections.map((section) => {
            const isOpen = openSections.has(section.id)
            const c = colorMap[section.color] || colorMap.sky
            const st = statusConfig[section.status]
            const Icon = section.icon

            return (
              <div
                key={section.id}
                id={`section-${section.id}`}
                className={`rounded-xl border ${c.border} ${c.bg} overflow-hidden transition-all scroll-mt-24`}
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-4 px-6 py-5 text-left group"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.pill}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] text-zinc-600 font-medium">#{section.number}</span>
                      <h2 className="text-[15px] font-semibold text-white truncate">{section.title}</h2>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${st.bg} ${st.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                        {st.label}
                      </span>
                    </div>
                    <p className="text-[12px] text-zinc-500 mt-1 truncate">{section.subtitle}</p>
                  </div>
                  <div className="flex-shrink-0 text-zinc-600 group-hover:text-zinc-400 transition-colors">
                    {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-6 pb-6 space-y-5">
                    <div className={`rounded-lg border p-4 ${c.bannerBg}`}>
                      <p className="text-[12px] text-zinc-400 leading-relaxed">{section.statusBanner}</p>
                    </div>

                    {(section.purpose ||
                      (section.implementationConsiderations && section.implementationConsiderations.length > 0) ||
                      (section.dependencies && section.dependencies.length > 0) ||
                      section.roadmapPlacement ||
                      (section.doneCriteria && section.doneCriteria.length > 0)) && (
                      <div className="space-y-3 rounded-lg border border-white/[0.06] bg-black/20 p-4">
                        {section.purpose && (
                          <div>
                            <h4 className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">
                              Purpose / goal
                            </h4>
                            <p className="text-[12px] text-zinc-400 leading-relaxed">{section.purpose}</p>
                          </div>
                        )}
                        {section.implementationConsiderations && section.implementationConsiderations.length > 0 && (
                          <div>
                            <h4 className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">
                              Key implementation considerations
                            </h4>
                            <ul className="list-disc list-inside space-y-1 text-[12px] text-zinc-400 leading-relaxed">
                              {section.implementationConsiderations.map((line, i) => (
                                <li key={i}>{line}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {section.dependencies && section.dependencies.length > 0 && (
                          <div>
                            <h4 className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">
                              Dependencies
                            </h4>
                            <ul className="list-disc list-inside space-y-1 text-[12px] text-zinc-400 leading-relaxed">
                              {section.dependencies.map((line, i) => (
                                <li key={i}>{line}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {section.roadmapPlacement && (
                          <div>
                            <h4 className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">
                              Roadmap / order of operations
                            </h4>
                            <p className="text-[12px] text-zinc-400 leading-relaxed">{section.roadmapPlacement}</p>
                          </div>
                        )}
                        {section.doneCriteria && section.doneCriteria.length > 0 && (
                          <div>
                            <h4 className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">
                              What &quot;done&quot; looks like
                            </h4>
                            <ul className="list-disc list-inside space-y-1 text-[12px] text-zinc-400 leading-relaxed">
                              {section.doneCriteria.map((line, i) => (
                                <li key={i}>{line}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {section.decisions && section.decisions.length > 0 && (
                      <div className="space-y-3">
                        {section.decisions.map((d, i) => (
                          <div
                            key={i}
                            className="rounded-lg border border-amber-500/20 bg-amber-500/[0.06] p-4"
                          >
                            <div className="flex items-start gap-3">
                              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                              <div>
                                <h4 className="text-[13px] font-semibold text-amber-300">{d.title}</h4>
                                <p className="text-[12px] text-zinc-400 mt-1 leading-relaxed">{d.description}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="space-y-3">
                      {section.tasks.filter((t) => t.type === 'ai').length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Bot className="w-3.5 h-3.5 text-sky-400" />
                            <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                              Things I Can Build
                            </span>
                          </div>
                          <div className="space-y-2">
                            {section.tasks
                              .filter((t) => t.type === 'ai')
                              .map((task, i) => (
                                <div
                                  key={i}
                                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-start gap-3"
                                >
                                  <Bot className="w-4 h-4 text-sky-400 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-[13px] font-medium text-white">{task.title}</h4>
                                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-500/15 text-sky-300">
                                        AI Build
                                      </span>
                                    </div>
                                    <p className="text-[12px] text-zinc-500 mt-1.5 leading-relaxed">{task.description}</p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {section.tasks.filter((t) => t.type === 'manual').length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-3 mt-4">
                            <UserCheck className="w-3.5 h-3.5 text-violet-400" />
                            <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                              Things Eddie Needs to Do
                            </span>
                          </div>
                          <div className="space-y-2">
                            {section.tasks
                              .filter((t) => t.type === 'manual')
                              .map((task, i) => (
                                <div
                                  key={i}
                                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-start gap-3"
                                >
                                  <UserCheck className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-[13px] font-medium text-white">{task.title}</h4>
                                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-500/15 text-violet-300">
                                        Eddie
                                      </span>
                                    </div>
                                    <p className="text-[12px] text-zinc-500 mt-1.5 leading-relaxed">{task.description}</p>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* ── Execution Order ──────────────────────────────────────────────── */}
        <div className="pt-8">
          <h2 className="text-lg font-semibold text-white mb-2">Recommended Execution Order</h2>
          <p className="text-[12px] text-zinc-500 mb-8">
            Phases 1–4 are product development. Phases 5–7 are platform, operations, and infrastructure — run in parallel with product work. Foundation work (rate limits, AI context schema, API standards) should start early so later features inherit protections and context.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {phases.map((phase) => {
              const c = colorMap[phase.color] || colorMap.sky
              return (
                <div
                  key={phase.number}
                  className={`rounded-xl border ${c.border} ${c.bg} p-6`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold ${c.pill}`}>
                      {phase.number}
                    </span>
                    <div>
                      <h3 className="text-[14px] font-semibold text-white">{phase.title}</h3>
                      <p className="text-[11px] text-zinc-500">{phase.subtitle}</p>
                    </div>
                  </div>
                  <ul className="space-y-2.5">
                    {phase.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${c.text.replace('text-', 'bg-')}`} />
                        <span className="text-[12px] text-zinc-400 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>
        </div>

        {/* ── End-of-Month Outcomes ────────────────────────────────────────── */}
        <div className="pt-8">
          <h2 className="text-lg font-semibold text-white mb-2">End-of-Month Outcomes</h2>
          <p className="text-[12px] text-zinc-500 mb-6">
            Each outcome mapped against current build status.
          </p>
          <div className="space-y-3">
            {outcomes.map((outcome, i) => {
              const st = statusConfig[outcome.status]
              return (
                <div
                  key={i}
                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <Target className="w-4 h-4 text-zinc-500" />
                    <span className="text-[13px] text-zinc-300">{outcome.label}</span>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${st.bg} ${st.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                    {st.label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* ── Codebase Audit ───────────────────────────────────────────────── */}
        <div className="pt-8">
          <h2 className="text-lg font-semibold text-white mb-2">Codebase Audit Summary</h2>
          <p className="text-[12px] text-zinc-500 mb-6">
            What the original plan called for vs. where things actually stand after your recent work.
          </p>
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left px-5 py-3 text-zinc-500 font-medium uppercase tracking-wider">Original Plan Item</th>
                  <th className="text-left px-5 py-3 text-zinc-500 font-medium uppercase tracking-wider">Actual Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {[
                  { feature: 'Promoter Dashboard (separate page)', status: 'Replaced — consolidated experience', done: true },
                  { feature: 'Manager Dashboard (separate page)', status: 'Replaced — consolidated experience', done: true },
                  { feature: 'Promoter ↔ Artist Sync System', status: 'Replaced — toggle on/off in Team Hub', done: true },
                  { feature: 'Multi-team filtering', status: 'Complete — Shows, Music, Tasks, Money', done: true },
                  { feature: 'Activity system', status: 'Complete — dual-write, approve/deny, progress', done: true },
                  { feature: 'Show guarantee → Money', status: 'Complete — backend cron + frontend', done: true },
                  { feature: 'Backend analytics tracking', status: 'Complete — sessions, page views, interactions', done: true },
                  { feature: 'Backend admin endpoints', status: 'Partial — login, users (need more)', done: false },
                  { feature: 'DIZEE Links', status: 'Built, hidden sidebar, localStorage only', done: false },
                  { feature: 'Money enhancements', status: 'Needs summary boxes, filters, statuses, attachments', done: false },
                  { feature: 'Show → Money (beyond guarantees)', status: 'Only guarantees flow — need bonuses, commissions', done: false },
                  { feature: 'Ticket history log', status: 'Not built', done: false },
                  { feature: 'Intelligence layer', status: 'Not built', done: false },
                  { feature: 'GA4 (Google Analytics)', status: 'Not built (custom analytics exists)', done: false },
                  { feature: 'Admin dashboard UI', status: 'Not built (backend endpoints exist)', done: false },
                  { feature: 'Staging environment', status: 'Not built', done: false },
                  { feature: 'System monitoring', status: 'Not built', done: false },
                  { feature: 'Granular permissions (backend enforcement)', status: 'Gaps found — frontend-only gating in several areas', done: false },
                  { feature: 'Tutorial / onboarding in new app', status: 'Not built (legacy tour exists)', done: false },
                  { feature: 'Welcome email', status: 'Not built', done: false },
                  { feature: 'Onboarding email sequence', status: 'Not built', done: false },
                  { feature: 'Promoter invite email', status: 'Not built (record created, no email)', done: false },
                  { feature: 'Weekly digest email', status: 'Not built (in-app only)', done: false },
                  { feature: 'Team invite email UX', status: 'Needs improvement (shows beta password)', done: false },
                  { feature: 'Centralized rate limits & quotas', status: 'Not built', done: false },
                  { feature: 'AI context & persistent memory layer', status: 'Not built', done: false },
                  { feature: 'Claude-first AI + API standardization', status: 'Not built', done: false },
                  { feature: 'Inbound email ingestion (CC/forward)', status: 'Not built', done: false },
                  { feature: 'Operational SOPs (deploy, incidents, access)', status: 'Not built', done: false },
                  { feature: 'Full-stack refactor (dizee + backend-dizee) + infra/SOP alignment', status: 'Not built', done: false },
                ].map((row, i) => (
                  <tr key={i}>
                    <td className="px-5 py-3 text-zinc-300">{row.feature}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[11px] font-medium ${
                        row.done ? 'text-emerald-400' :
                        row.status.includes('Not built') ? 'text-rose-400' :
                        'text-amber-400'
                      }`}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/[0.06] mt-16">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <span className="text-[11px] text-zinc-600">Confidential — Internal Use Only</span>
          <span className="text-[11px] text-zinc-700">Audited: April 2026 (frontend + backend-dizee)</span>
        </div>
      </footer>
    </div>
  )
}
